/**
 * Consent-aware visitor analytics — the ONE analytics module (loader + dataLayer).
 *
 * Ported from canideal.org PR #128 (assets/site-analytics.js):
 * - Runs only on the production host (SITE_URL, with or without www) over HTTPS;
 *   Vercel previews, localhost and other hosts never load a tracker.
 * - Global Privacy Control / Do Not Track disable both trackers.
 * - HubSpot tracking (portal from constants) loads once; its cookie banner owns
 *   consent. Google uses consent mode (all storage denied by default) and its
 *   script — the GTM container, or GA4 when no container is configured — is only
 *   loaded after HubSpot's privacy listener grants analytics. Revocation sets
 *   analytics_storage back to denied. Advertising storage stays denied.
 * - Google page URLs/referrers drop every query parameter except UTM fields
 *   (no `_hsenc`/`_hsmi` tokens, emails or form fields).
 *
 * IDs come from env only: NEXT_PUBLIC_GTM_ID (GTM-…) or NEXT_PUBLIC_GA4_ID (G-…).
 * Diagnostics: `window.__cidSiteAnalytics` (status only, no visitor identity).
 */

import { HUBSPOT_PORTAL_ID, HUBSPOT_TRACKING_SRC, SITE_URL } from '@/lib/constants';

export type GoogleTag = { kind: 'gtm' | 'ga4'; id: string };

export interface SiteAnalyticsConfig {
  /** Case-insensitive pattern for production hostnames. */
  hostPattern: string;
  hubspotPortalId: string;
  hubspotSrc: string;
  /** Exactly one Google loader (GTM wins, so GA4 is never loaded twice). */
  google: GoogleTag | null;
}

const GTM_ID = /^GTM-[A-Z0-9]{4,}$/;
const GA4_ID = /^G-[A-Z0-9]{5,}$/;

type AnalyticsEnv = Partial<Record<'NEXT_PUBLIC_GTM_ID' | 'NEXT_PUBLIC_GA4_ID', string>>;

/** Validated config. Invalid IDs are ignored (never injected into the page). */
export function getSiteAnalyticsConfig(
  // Literal property reads so Next inlines NEXT_PUBLIC_* at build time.
  env: AnalyticsEnv = {
    NEXT_PUBLIC_GTM_ID: process.env.NEXT_PUBLIC_GTM_ID,
    NEXT_PUBLIC_GA4_ID: process.env.NEXT_PUBLIC_GA4_ID,
  }
): SiteAnalyticsConfig {
  const gtm = (env.NEXT_PUBLIC_GTM_ID || '').trim();
  const ga4 = (env.NEXT_PUBLIC_GA4_ID || '').trim();
  const host = new URL(SITE_URL).hostname.replace(/^www\./, '');
  let google: GoogleTag | null = null;
  if (GTM_ID.test(gtm)) google = { kind: 'gtm', id: gtm };
  else if (GA4_ID.test(ga4)) google = { kind: 'ga4', id: ga4 };
  return {
    hostPattern: `^(?:www\\.)?${host.replace(/\./g, '\\.')}$`,
    hubspotPortalId: HUBSPOT_PORTAL_ID,
    hubspotSrc: HUBSPOT_TRACKING_SRC,
    google,
  };
}

// Plain ES5 (inlined verbatim into <head>); keep in step with canideal.org's loader.
const BOOTSTRAP = `function (c) {
  var w = window, d = document;
  if (w.location.protocol !== 'https:' || !new RegExp(c.hostPattern, 'i').test(w.location.hostname)) return;
  if (w.__cidSiteAnalytics) return;
  var state = w.__cidSiteAnalytics = { status: 'initializing', hubspot: 'not-loaded', google: 'not-loaded',
    tag: c.google ? c.google.kind : 'none' };
  var nav = w.navigator || {};
  if (nav.globalPrivacyControl === true || nav.doNotTrack === '1' || w.doNotTrack === '1') {
    state.status = 'browser-opt-out';
    return;
  }
  var g = c.google, started = false;
  var utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];
  w.dataLayer = w.dataLayer || [];
  w.gtag = w.gtag || function () { w.dataLayer.push(arguments); };
  if (g) {
    w.gtag('consent', 'default', {
      analytics_storage: 'denied', ad_storage: 'denied',
      ad_user_data: 'denied', ad_personalization: 'denied'
    });
  }
  function clean(raw) {
    if (!raw) return '';
    try {
      var url = new URL(raw, w.location.origin), out = new URL(url.origin + url.pathname);
      utmKeys.forEach(function (key) {
        var value = url.searchParams.get(key);
        if (value && value.length <= 200 && !/@|%40/i.test(value)) out.searchParams.set(key, value);
      });
      return out.href;
    } catch (_) { return ''; }
  }
  function google(allowed) {
    if (!g) { state.google = 'not-configured'; return; }
    if (g.kind === 'ga4') w['ga-disable-' + g.id] = !allowed;
    if (!allowed && !started) { state.google = 'awaiting-consent'; return; }
    if (!started) {
      started = true;
      w.gtag('consent', 'update', { analytics_storage: 'granted' });
      var src;
      if (g.kind === 'ga4') {
        w.gtag('js', new Date());
        w.gtag('config', g.id, {
          page_location: clean(w.location.href), page_referrer: clean(d.referrer || ''),
          allow_google_signals: false, allow_ad_personalization_signals: false,
          cookie_flags: 'SameSite=Lax;Secure'
        });
        src = 'https://www.googletagmanager.com/gtag/js?id=' + g.id;
      } else {
        w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
        src = 'https://www.googletagmanager.com/gtm.js?id=' + g.id;
      }
      if (!d.querySelector('script[src^="https://www.googletagmanager.com/"]')) {
        var s = d.createElement('script');
        s.id = 'cid-google-tag';
        s.async = true;
        s.src = src;
        s.onerror = function () { state.google = 'load-failed'; };
        d.head.appendChild(s);
      }
    } else {
      w.gtag('consent', 'update', { analytics_storage: allowed ? 'granted' : 'denied' });
    }
    state.google = allowed ? 'enabled' : 'consent-revoked';
  }
  state.status = 'awaiting-privacy-policy';
  state.google = g ? 'awaiting-consent' : 'not-configured';
  w._hsq = w._hsq || [];
  w._hsq.push(['doNotTrack']);
  w._hsp = w._hsp || [];
  w._hsp.push(['addPrivacyConsentListener', function (consent) {
    var allowed = !!(consent && (consent.categories
      ? consent.categories.analytics === true : consent.allowed === true));
    w._hsq = w._hsq || [];
    w._hsq.push(allowed ? ['doNotTrack', { track: true }] : ['doNotTrack']);
    google(allowed);
    state.status = allowed ? 'analytics-consent-granted' : 'analytics-consent-not-granted';
  }]);
  if (d.getElementById('hs-script-loader') ||
      d.querySelector('script[src*="hs-scripts.com/' + c.hubspotPortalId + '.js"]')) {
    state.hubspot = 'existing-loader';
  } else {
    var hs = d.createElement('script');
    hs.id = 'hs-script-loader';
    hs.async = true;
    hs.defer = true;
    hs.src = c.hubspotSrc;
    hs.onload = function () { state.hubspot = 'loaded'; };
    hs.onerror = function () { state.hubspot = 'load-failed'; };
    d.head.appendChild(hs);
    state.hubspot = 'loading';
  }
}`;

/** Inline <head> script; JSON-escaped so config can never break out of the tag. */
export function siteAnalyticsScript(
  config: SiteAnalyticsConfig = getSiteAnalyticsConfig()
): string {
  const json = JSON.stringify(config).replace(/</g, '\\u003c');
  return `(${BOOTSTRAP})(${json});`;
}

// ─── Events (dataLayer for GTM, gtag for GA4) ─────────────────────────────────

type EventParams = Record<string, unknown>;

interface SiteAnalyticsState {
  status: string;
  hubspot: string;
  google: string;
  tag: GoogleTag['kind'] | 'none';
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __cidSiteAnalytics?: SiteAnalyticsState;
  }
}

/** Active Google tag once the loader ran on production, the browser did not opt out, and consent is granted. */
function activeTag(): GoogleTag['kind'] | null {
  if (typeof window === 'undefined') return null;
  const state = window.__cidSiteAnalytics;
  if (
    !state ||
    state.status === 'browser-opt-out' ||
    state.status !== 'analytics-consent-granted' ||
    state.tag === 'none'
  ) {
    return null;
  }
  return state.tag;
}

/**
 * Track a custom event. Queued until consent loads Google (GTM: dataLayer
 * object; GA4: gtag event). No-op on previews, localhost and opted-out browsers.
 */
export function trackEvent(eventName: string, eventParams: EventParams = {}): void {
  const tag = activeTag();
  if (tag === 'gtm') window.dataLayer?.push({ event: eventName, ...eventParams });
  else if (tag === 'ga4') window.gtag?.('event', eventName, eventParams);
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];

/** Path + UTM-only query: no HubSpot recipient tokens, emails or form fields. */
export function sanitizePagePath(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  const kept = new URLSearchParams();
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value && value.length <= 200 && !/@|%40/i.test(value)) kept.set(key, value);
  }
  const query = kept.toString();
  return query ? `${pathname}?${query}` : pathname;
}

let lastTrackedPath: string | null = null;

/**
 * One `page_view` per distinct URL for the GTM container (StrictMode/re-render
 * safe). GA4 needs none: its config sends the first page view and enhanced
 * measurement records client-side history changes.
 */
export function trackPageView(pagePath: string, pageTitle: string): void {
  if (activeTag() !== 'gtm' || pagePath === lastTrackedPath) return;
  lastTrackedPath = pagePath;
  trackEvent('page_view', { page_path: pagePath, page_title: pageTitle });
}
